import { ShippingDetails } from '@/models/shipping-details';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiService } from '@services/api.service';
import { AppService } from '@services/app.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-add-shipping-details',
  templateUrl: './add-shipping-details.component.html',
  styleUrl: './add-shipping-details.component.scss'
})
export class AddShippingDetailsComponent implements OnInit {
  shippingForm: FormGroup;
  shippingList: ShippingDetails[] = [];
  editingId: number | null = null;
  destroy$: Subject<boolean> = new Subject<boolean>();
  editId: number | null = null;
  searchText: any;

  states = ['Tamil Nadu', 'South India', 'North India'];

  constructor(private fb: FormBuilder, private appService: AppService, private spinner: NgxSpinnerService,
    private toster: ToastrService,
  ) {
    this.shippingForm = this.fb.group({
      shippingId: [0],
      shippingPartner: ['', [Validators.required, Validators.maxLength(10)]],
      state: ['', [Validators.required, Validators.minLength(3)]],
      amount: [0, [Validators.required, Validators.pattern(/^[0-9]+$/)]],
      isActive: ['true', Validators.required],
      districtCode: [''],
      createdAt: [0],
      updatedAt: [0],
    });
  }

  ngOnInit(): void {
    this.getAll();
  }

  ngOnDestroy(): void {
    this.destroy$.complete();
  }



  /* ---------------- GET ALL ---------------- */
  getAll() {
    this.spinner.show();
    this.appService.getAllShipping()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.shippingList = res.sort((a, b) => b.shippingId - a.shippingId);
          this.spinner.hide();
        },
        error: () => {
          this.spinner.hide();
          this.toster.error('Failed to load data');
        }
      });
  }

  allowOnlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  /* ---------------- EDIT ---------------- */
  edit(row: ShippingDetails) {
    this.editId = row.shippingId;
    this.shippingForm.patchValue(row);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /* ---------------- SAVE / UPDATE ---------------- */
  save() {
    if (this.shippingForm.invalid) {
      this.shippingForm.markAllAsTouched();
      return;
    }

    this.spinner.show();
    const payload = this.shippingForm.value;

    // UPDATE
    if (this.editId) {
      this.appService.updateShippingDetails(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            const index = this.shippingList.findIndex(
              x => x.shippingId === res.shippingId
            );
            if (index !== -1) {
              this.shippingList[index] = res; // update row
            }
            this.afterSave('Details updated successfully.');
          },
          error: (err) => this.handleError(err)
        });
    }

    // SAVE
    else {
      this.appService.saveShippingDetails(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            this.shippingList.unshift(res); // push to table
            this.afterSave('Details saved successfully.');
          },
          error: (err) => this.handleError(err)
        });
    }
  }

  /* ---------------- COMMON ---------------- */
  afterSave(message: string) {
    this.spinner.hide();
    this.toster.success(message);
    this.resetForm();
  }

  handleError(error: any) {
    this.spinner.hide();
    this.toster.error(
      error?.error?.message || 'Something went wrong, please try again.'
    );
  }

  resetForm() {
    this.editId = null;
    this.shippingForm.reset({
      shippingId: 0,
      isActive: true,
      shippingPartner: '',
      state: '',
      amount: 0
    });
  }

  // Helper for template validation
  isInvalid(field: string) {
    const control = this.shippingForm.get(field);
    return control?.invalid && (control.dirty || control.touched);
  }
}
